import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { apiClient, type ApiPaginatedResponse } from "./_client";

export interface DashboardOwner {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface DashboardWidget {
  id: string;
  name: string;
  widget_type: string;
  widget_type_display: string;
  config: Record<string, unknown>;
  col_span: number;
  row_span: number;
  order: number;
  data: unknown;
  source_data: {
    projects?: Array<{ id: string; name: string }>;
    portfolios?: Array<{ id: string; name: string }>;
    reports?: Array<{
      id: string;
      title: string;
      analysis_id: string;
      created_at: string;
    }>;
    documents?: Array<{ id: string; name: string }>;
    analyses?: Array<{ id: string; title: string }>;
  };
  created_at: string;
  updated_at: string;
}

export interface Dashboard {
  id: string;
  name: string;
  description: string | null;
  organization: string;
  created_at: string;
  updated_at: string;
  created_by: DashboardOwner | null;
  widgets: DashboardWidget[];
  // Template fields
  is_template?: boolean;
  template_name?: string;
  template_description?: string;
  source_template?: string | null;
}

type DashboardPaginatedResponse = ApiPaginatedResponse<Dashboard>;

export const useDashboards = (
  pageSize: number = 10,
  page: number = 1,
  projectId?: string,
) =>
  useQuery<DashboardPaginatedResponse>({
    queryKey: ["dashboards", pageSize, page, projectId],
    queryFn: async () => {
      let url = `api/dashboards/?page=${page}&page_size=${pageSize}`;
      if (projectId) {
        url += `&project_id=${projectId}`;
      }
      const response = await apiClient.get<
        never,
        AxiosResponse<DashboardPaginatedResponse>
      >(url);
      return response.data;
    },
    staleTime: 30 * 1000,
  });

export const getDashboard = async (dashboardId: string) => {
  const response = await apiClient.get<never, AxiosResponse<Dashboard>>(
    `api/dashboards/${dashboardId}/`,
  );
  return response.data;
};

export const useDashboard = (dashboardId?: string) =>
  useQuery<Dashboard>({
    queryKey: ["dashboard", dashboardId],
    queryFn: () => getDashboard(dashboardId!),
    enabled: !!dashboardId,
    staleTime: 30 * 1000,
  });

export const deleteDashboard = async (dashboardId: string) => {
  const response = await apiClient.delete(`api/dashboards/${dashboardId}/`);
  return response.data;
};

export const useDeleteDashboard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDashboard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
};

// Reorder widgets within a dashboard
export const reorderWidgets = async (
  dashboardId: string,
  widgetOrder: string[],
) => {
  const response = await apiClient.post(
    `api/dashboards/${dashboardId}/reorder-widgets/`,
    { widget_order: widgetOrder },
  );
  return response.data;
};

export const useReorderWidgets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetOrder,
    }: {
      dashboardId: string;
      widgetOrder: string[];
    }) => reorderWidgets(dashboardId, widgetOrder),
    onMutate: async ({ dashboardId, widgetOrder }) => {
      await queryClient.cancelQueries({
        queryKey: ["dashboard", dashboardId],
      });
      const previous = queryClient.getQueryData<Dashboard>([
        "dashboard",
        dashboardId,
      ]);
      if (previous) {
        const reordered = widgetOrder
          .map((id) => previous.widgets.find((w) => w.id === id))
          .filter((w): w is DashboardWidget => !!w);
        queryClient.setQueryData<Dashboard>(["dashboard", dashboardId], {
          ...previous,
          widgets: reordered,
        });
      }
      return { previous };
    },
    onError: (_err, { dashboardId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["dashboard", dashboardId],
          context.previous,
        );
      }
    },
    onSettled: (_, __, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
    },
  });
};

// Delete widget from a dashboard
export const deleteWidget = async (dashboardId: string, widgetId: string) => {
  const response = await apiClient.post(
    `api/dashboards/${dashboardId}/delete-widget/`,
    { widget_id: widgetId },
  );
  return response.data as { status: string; deleted_widget: string };
};

export const useDeleteWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
    }: {
      dashboardId: string;
      widgetId: string;
    }) => deleteWidget(dashboardId, widgetId),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
};

// Update widget properties (name, size, value, prefix, suffix, config)
export interface UpdateWidgetParams {
  dashboardId: string;
  widgetId: string;
  name?: string;
  col_span?: number;
  row_span?: number;
  value?: number;
  prefix?: string;
  suffix?: string;
  config?: Record<string, unknown>;
}

export const updateWidget = async ({
  dashboardId,
  widgetId,
  ...updates
}: UpdateWidgetParams) => {
  const response = await apiClient.post(
    `api/dashboards/${dashboardId}/update-widget/`,
    { widget_id: widgetId, ...updates },
  );
  return response.data as {
    status: string;
    widget_id: string;
    updated_fields: string[];
    message: string;
  };
};

export const useUpdateWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: UpdateWidgetParams) => updateWidget(params),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
};

// Undo widget update (reverts to previous state via django-simple-history)
export const undoWidget = async (dashboardId: string, widgetId: string) => {
  const response = await apiClient.post(
    `api/dashboards/${dashboardId}/undo-widget/`,
    { widget_id: widgetId },
  );
  return response.data as { status: string; widget_id: string; message: string };
};

export const useUndoWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
    }: {
      dashboardId: string;
      widgetId: string;
    }) => undoWidget(dashboardId, widgetId),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
};

// Template operations
export const saveAsTemplate = async (
  dashboardId: string,
  templateName: string,
  description?: string,
) => {
  const response = await apiClient.post(
    `api/dashboards/${dashboardId}/save-as-template/`,
    { template_name: templateName, description },
  );
  return response.data;
};

export const useSaveAsTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      templateName,
      description,
    }: {
      dashboardId: string;
      templateName: string;
      description?: string;
    }) => saveAsTemplate(dashboardId, templateName, description),
    onSuccess: (_, { dashboardId }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", dashboardId] });
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-templates"] });
    },
  });
};

export const createFromTemplate = async (
  templateId: string,
  name: string,
) => {
  const response = await apiClient.post(`api/dashboards/from-template/`, {
    template_id: templateId,
    name,
  });
  return response.data as { dashboard_id: string; name: string; widget_count: number };
};

export const useCreateFromTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name: string }) =>
      createFromTemplate(templateId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
};

export interface DashboardTemplateSummary {
  id: string;
  name: string;
  template_name: string;
  template_description: string;
  updated_at: string;
  widget_count: number;
}

export const listTemplates = async (search?: string) => {
  const params = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await apiClient.get<
    never,
    AxiosResponse<DashboardTemplateSummary[]>
  >(`api/dashboards/templates/${params}`);
  return response.data;
};

export const useDashboardTemplates = (search?: string) =>
  useQuery<DashboardTemplateSummary[]>({
    queryKey: ["dashboard-templates", search],
    queryFn: () => listTemplates(search),
    staleTime: 60 * 1000,
  });

// Template management hooks
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { template_name?: string; template_description?: string };
    }) => {
      const response = await apiClient.patch(
        `api/dashboards/${id}/update-template/`,
        data,
      );
      return response.data as Dashboard;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-templates"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-template-preview", id] });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`api/dashboards/${id}/delete-template/`);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-templates"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-template-preview", id] });
    },
  });
};

export const useTemplatePreview = (id: string | null) =>
  useQuery<Dashboard>({
    queryKey: ["dashboard-template-preview", id],
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<Dashboard>>(
        `api/dashboards/${id}/template-preview/`,
      );
      return response.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });

// Export functions - handle both blob success and JSON error responses
const handleBlobResponse = async (response: AxiosResponse): Promise<Blob> => {
  // Check if the response is actually JSON (error response)
  const contentType = response.headers["content-type"] || "";
  if (contentType.includes("application/json")) {
    // Parse the blob as JSON to get the error message
    const text = await response.data.text();
    const error = JSON.parse(text);
    throw new Error(error.error || "Export failed");
  }
  return response.data;
};

export const exportDashboardDocx = async (dashboardId: string): Promise<Blob> => {
  const response = await apiClient.get(
    `api/dashboards/${dashboardId}/export-docx/`,
    { responseType: "blob" },
  );
  return handleBlobResponse(response);
};

export const exportDashboardPptx = async (dashboardId: string): Promise<Blob> => {
  const response = await apiClient.get(
    `api/dashboards/${dashboardId}/export-pptx/`,
    { responseType: "blob" },
  );
  return handleBlobResponse(response);
};

/**
 * Download a Blob as a file.
 *
 * Creates a temporary link element and triggers download.
 */
export function downloadBlobAsFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
